import * as React from "react";
import { dragDimensionValue, toggleDimensionValue } from "actions/tool-panel/draft/actions";
import { DragDimensionValue, ToggleDimensionValue } from "actions/tool-panel/draft/types";
import { ListDraggableItem } from "components/common/list-draggable-item";
import { connect } from "react-redux";
import { IStore } from "reducers";
import { DimensionsPanelReduxState } from "reducers/dimensions-panel/dimensions-panel";
import { SelectedDimension } from "../dimensions-tool-panel";
import { DimensionValueCard } from "./DimensionValueCard";
import { SelectorHeaderContainer } from "./SelectorHeader";
import { useVirtualListCache, VirtualCachedRowList } from "components/common/virtual-list";

const APPLY_BUTTON_CONTAINER_HEIGHT = 50;
type ReduxProps = {
    draftState: DimensionsPanelReduxState;
    dragDimensionValueAction: DragDimensionValue;
    toggleDimensionValueAction: ToggleDimensionValue;
};

type OwnProps = {
    selectedDimension: SelectedDimension;
};

const DimensionValuesSelector: React.FC<ReduxProps & OwnProps> = ({
    draftState,
    selectedDimension,
    dragDimensionValueAction,
    toggleDimensionValueAction,
}) => {
    // this can be useDimension(selectedDimension, draftState) => [dimension, dimensionIndex]
    const selectedDimensionKind = selectedDimension.kind;
    const dimensionTypeToUpdate = draftState[selectedDimensionKind];

    const dimensionIndex = dimensionTypeToUpdate.findIndex(({ id }) => id === selectedDimension.id);
    const dimension = dimensionTypeToUpdate[dimensionIndex];

    const areAllValuesSelected = dimension.values.every((value) => value.selected);

    const virtualListCache = useVirtualListCache({
        cacheOptions: {
            defaultHeight: 40,
            fixedWidth: true,
            minHeight: 40,
        },
    });
    const { recomputeRowHeight, clearRowCache } = virtualListCache;

    const onMoveCallback = React.useCallback(
        (dragIndex: number, hoverIndex: number) => {
            clearRowCache(dragIndex);
            clearRowCache(hoverIndex);
            recomputeRowHeight(dragIndex);
            recomputeRowHeight(hoverIndex);
        },
        [clearRowCache, recomputeRowHeight]
    );

    const onMoveHandler = React.useCallback(
        (dragIndex: number, hoverIndex: number) => {
            dragDimensionValueAction({
                selectionKind: selectedDimensionKind,
                dimensionIndex,
                dragIndex,
                hoverIndex,
            });
            onMoveCallback(dragIndex, hoverIndex);
        },
        [dimensionIndex, selectedDimensionKind, dragDimensionValueAction, onMoveCallback]
    );
    const onCheckHandler = React.useCallback(
        (checked: boolean, index: number) =>
            toggleDimensionValueAction({ selectionKind: selectedDimensionKind, dimensionIndex, index, checked }),
        [toggleDimensionValueAction, selectedDimensionKind, dimensionIndex]
    );

    return (
        <>
            <SelectorHeaderContainer
                order={dimension.sort}
                areAllItemsSelected={areAllValuesSelected}
                selectionKind={selectedDimension.kind}
                dimensionIndex={dimensionIndex}
            />
            <VirtualCachedRowList
                data={dimension.values}
                hookInstance={virtualListCache}
                customWrapperCSS={{ height: `calc(100% - ${APPLY_BUTTON_CONTAINER_HEIGHT}px)`, padding: "0px 10px" }}
            >
                {({ element, renderProps }) => {
                    const { index } = renderProps;
                    return (
                        <ListDraggableItem id={element.id} index={index} key={element.id} onMove={onMoveHandler}>
                            {(props) => (
                                <DimensionValueCard
                                    text={element.label}
                                    id={element.id}
                                    checked={element.selected}
                                    onCheck={(_, checked) => onCheckHandler(checked, index)}
                                    {...props}
                                />
                            )}
                        </ListDraggableItem>
                    );
                }}
            </VirtualCachedRowList>
        </>
    );
};

export const DimensionValuesSelectorContainer = connect(
    (state: IStore) => ({
        draftState: state.dimensionsPanel.draft,
    }),
    { dragDimensionValueAction: dragDimensionValue, toggleDimensionValueAction: toggleDimensionValue }
)(DimensionValuesSelector) as unknown as React.FC<OwnProps>;
